import DefaultTemplate from '@templates/DefaultTemplate';
import { Wrapper } from './ProfilePage.styled';
import { useEffect, useRef, useState } from 'react';
import db from '@utils/db';
import { useStore } from '@store/appStore';
import { useAuthentication } from '@providers/AuthProvider';
import { useRouter } from 'next/router';
import Profile from '@components/Profile';
import moment from 'moment';
import { canSendWithToken } from '@utils/tokenRefresh';

// How often an open, visible profile page asks whether its data changed (CheckForStudentUpdate).
// Was 5s: ~22% of all calls to the biggest school's webservice (361k of 1.67M in 30 days), almost all
// answering "no change", plus a phone radio kept awake. The student's own check-in and returning to the
// page both check immediately, so this only bounds how fast e.g. a teacher's registration shows up.
const UPDATE_POLL_INTERVAL_MS = 30 * 1000;

export const ProfilePage = (props: { isTeacher?: boolean }) => {
  const [badgeSplash, setBadgeSplash] = useState(null);
  const [total, setTotal] = useState(null);
  const [day, setDay] = useState(null);
  const [courses, setCourses] = useState(null);
  const [load, setLoad] = useState(false);
  const { client, whoIsLoggedIn, filterDate, settings, ids, checkin } = useStore();
  const { getProfile } = useAuthentication();
  const [info, setinfo] = useState({ firstname: whoIsLoggedIn.firstname, lastname: whoIsLoggedIn.lastname });
  const { pathname } = useRouter();
  const profile = useRef(null);
  const [update, setUpdate] = useState(null)
  const updater = useRef(null)
  // The last load got no answer (e.g. the app was opened without a connection) - the next update check
  // reloads everything instead of waiting for the server to report a change.
  const loadFailed = useRef(false)
  const [open, setOpen] = useState(false)
  let id = sessionStorage.getItem('demo') || pathname === '/profile' ? '48637' : null;
  

  async function awaitProfile() {
    const profile = await getProfile().catch(() => null); // no token / Graph down: keep the name shown
    if (profile) {
      if (profile.givenName || profile.surname) {
        setinfo({ firstname: profile.givenName, lastname: profile.surname });
      } else {
        setinfo({ firstname: profile.displayName, lastname: '' });
      }
    }
  }  

  async function getData() {
    let userId = id;
    if (!id) {
      userId = client.UserName;
    }
    
    const totalData = await db.Student_Stat_GetTotalPercentage(userId, filterDate.from, filterDate.to, settings.USEMERIT, settings.USEPAUSE);
    const totalabsence = totalData ? totalData[0] : null;
    if (!totalabsence) {
      // No answer (e.g. opened without a connection) - keep what's shown; see loadFailed.
      loadFailed.current = true;
      setLoad(false);
      return;
    }
    loadFailed.current = false;
    if(settings.USENOTFULLBLOCKPAUSE)
    {
      let pauseStudent = await db.GetPause4Students(JSON.stringify([{student_id: id}]), filterDate.from, filterDate.to)
        if(pauseStudent?.length > 0)
        {            
          //@ts-ignore
          totalabsence.secondabsence = totalabsence.secondabsence - pauseStudent[0].pause_absence_length
          //@ts-ignore
          totalabsence.secondcount = totalabsence.secondcount - pauseStudent[0].pause_length
          //@ts-ignore
          totalabsence.merit = true             
        }
    }
    
    setTotal(totalabsence);
    console.log(totalabsence);
    //@ts-ignore
    if(totalabsence.absencelessons === null && totalabsence.lessoncount === null && totalabsence.lessonspercentage === null && totalabsence.secondabsence === null && totalabsence.secondcount === null && totalabsence.secondpercentage === null){
      const saved = localStorage.getItem("profilePop");
      if (saved !== null) {
        setOpen(false);
      }else {
        setOpen(true);
        localStorage.setItem("profilePop", "true");
      }
    }
    setLoad(false);    
    const dayData = await db.Student_Stat_GetTotalPercentageDay(userId, filterDate.from, filterDate.to, null, null, settings.USEMERIT, settings.USEPAUSE);
    setDay(dayData);    
  }
  console.log()

  useEffect(() => {
    if (!client) return;
    setLoad(true);
    getData();
    awaitProfile();
  }, [client, filterDate, update]);

  useEffect(() => {
      checkForUpdate()
      // Paused while the tab is hidden (see below) - check straight away when it's shown again.
      const onVisible = () => {
        if (document.visibilityState === 'visible') pollForUpdate()
      }
      document.addEventListener('visibilitychange', onVisible)
      return () => {
        clearInterval(updater.current)
        document.removeEventListener('visibilitychange', onVisible)
      }
    }, [])

  // Right after the student's own check-in: its registration has just queued an AppUpdate row (DB
  // trigger on RegistrationLog), so check now and reload every card at once rather than on the next
  // scheduled poll.
  useEffect(() => {
      if (checkin) pollForUpdate()
    }, [checkin])

  async function pollForUpdate() {
      // From the store at call time - the interval below is set up once at mount, and its closure's
      // settings/ids would otherwise stay whatever they were then.
      const { settings, ids } = useStore.getState()
      // Token expired and couldn't be renewed: anything sent now is refused - wait for the next round.
      if (!(await canSendWithToken())) return
      if (loadFailed.current) {
        // Reloads every card (and the calendar, via updateTime); getData clears the flag if it works now.
        setUpdate(moment().toDate())
        return
      }
      let update = await db.CheckForStudentUpdate(settings?.DEMO === true || settings?.DEMO === "true" ? '60249' : ids?.student_id)
      if (update?.length > 0 && update[0]?.result === true) {
        console.log("update")
        setUpdate(moment().toDate())
      }
      else {
        console.log("no-update")
      }
    }

  async function checkForUpdate() {
      updater.current = setInterval(async () => {
        // Nobody sees a hidden tab's updates, and a sleeping laptop/phone woken now and then can't
        // renew its token - polling there only produced expired-token 403s.
        if (document.hidden) return
        await pollForUpdate()
      }, UPDATE_POLL_INTERVAL_MS)
    }

  return (
    <DefaultTemplate>
      <Wrapper>
        <Profile
          ref={profile}
          id={id ? id : client.UserName}
          user_id={settings.DEMO === true || settings.DEMO === "true" ? '60249' : ids?.student_id}
          sisId={client.UserName}
          firstname={info.firstname}
          lastname={info.lastname}
          lastLogin={client.LASTLOGIN}
          day={day}
          total={total}
          updateTime={update}
          open={open}
          setOpen={setOpen}
        />
      </Wrapper>
    </DefaultTemplate>
  );
};

export default ProfilePage;
